import api from "../api";
import { fetchAllPages } from "./paginatedList";
import { readinessTag } from "./integrationsBeta";

export interface WebhookTrigger {
  id: number;
  integration: number;
  provider: string;
  name: string;
  connection: number;
  url_token: string;
  is_active: boolean;
  verification_code: string;
  webhook_url: string;
  created_at: string;
  updated_at: string;
  // Present ONLY in the create response: the auto-generated Secret-Key to
  // paste into the provider UI. Never returned by list/detail afterwards.
  secret_key?: string;
}

export interface WebhookSecret {
  secret_key: string;
}

const tag = (integrationId: number | "LIST") => ({
  type: "WebhookTrigger" as const,
  id: integrationId,
});

export const webhookTriggersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getWebhookTriggers: builder.query<WebhookTrigger[], number>({
      queryFn: (integrationId, _api, _extra, fetchWithBQ) =>
        fetchAllPages<WebhookTrigger>(
          fetchWithBQ,
          "/integration-platform/webhook-triggers/",
          { integration: integrationId },
        ),
      providesTags: (_result, _err, integrationId) => [tag(integrationId)],
    }),

    createWebhookTrigger: builder.mutation<
      WebhookTrigger,
      {
        integration: number;
        provider: string;
        name?: string;
      }
    >({
      query: (body) => ({
        url: "/integration-platform/webhook-triggers/",
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _err, { integration }) => [
        tag(integration),
        readinessTag(integration),
      ],
    }),

    updateWebhookTrigger: builder.mutation<
      WebhookTrigger,
      {
        id: number;
        integration: number;
        name?: string;
        is_active?: boolean;
      }
    >({
      query: ({ id, integration, ...body }) => ({
        url: `/integration-platform/webhook-triggers/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _err, { integration }) => [
        tag(integration),
        readinessTag(integration),
      ],
    }),

    rotateWebhookTrigger: builder.mutation<
      WebhookTrigger,
      { id: number; integration: number }
    >({
      query: ({ id }) => ({
        url: `/integration-platform/webhook-triggers/${id}/rotate-url/`,
        method: "POST",
      }),
      invalidatesTags: (_result, _err, { integration }) => [tag(integration)],
    }),

    // Read the trigger's current Secret-Key (superuser). Not cached - the
    // caller shows it on demand.
    revealWebhookSecret: builder.mutation<WebhookSecret, { id: number }>({
      query: ({ id }) => ({
        url: `/integration-platform/webhook-triggers/${id}/reveal-secret/`,
        method: "POST",
      }),
    }),

    // Generate a NEW Secret-Key and return it once (superuser).
    rotateWebhookSecret: builder.mutation<WebhookSecret, { id: number }>({
      query: ({ id }) => ({
        url: `/integration-platform/webhook-triggers/${id}/rotate-secret/`,
        method: "POST",
      }),
    }),

    deleteWebhookTrigger: builder.mutation<
      unknown,
      { id: number; integration: number }
    >({
      query: ({ id }) => ({
        url: `/integration-platform/webhook-triggers/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _err, { integration }) => [
        tag(integration),
        readinessTag(integration),
      ],
    }),

    redriveWebhookDlq: builder.mutation<{ task_handle: string }, void>({
      query: () => ({
        url: "/integration-platform/webhook-triggers/redrive-dlq/",
        method: "POST",
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetWebhookTriggersQuery,
  useCreateWebhookTriggerMutation,
  useUpdateWebhookTriggerMutation,
  useDeleteWebhookTriggerMutation,
  useRotateWebhookTriggerMutation,
  useRevealWebhookSecretMutation,
  useRotateWebhookSecretMutation,
  useRedriveWebhookDlqMutation,
} = webhookTriggersApi;
